import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Button, Heading } from "@foris/avocado-suite";
import { Instructor } from "@models/ISchema";
import { ContextApp } from "@config/Context/contextApp";
import { updateSlotInstructors } from "@context/useProgrammingEditStore";
import { SlotInstructor } from "@services/types/programming-edit/programmingEditTypes";
import InstructorPicker from "../instructor-picker";
import useInstructorSearch, { InstructorOption } from "../../hooks/useInstructorSearch";
import styles from "./assignInstructorDrawer.module.scss";
import { useLayoutStore } from "@context/useLayoutStore";

interface AssignInstructorDrawerProps {
  slotId: string;
  courseId?: string;
  currentInstructors: SlotInstructor[];
  isOpen: boolean;
  isReadOnly?: boolean;
  onClose: () => void;
}

const AssignInstructorDrawer: React.FC<AssignInstructorDrawerProps> = ({
  slotId,
  courseId,
  currentInstructors,
  isOpen,
  isReadOnly = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const { user } = useContext(ContextApp);
  const [shouldFilter, setShouldFilter] = useState(true);
  const [selected, setSelected] = useState<Instructor[]>([]);

  const { options, loading, onSearch } = useInstructorSearch({
    courseId,
    shouldFilter,
  });

  useEffect(() => {
    if (isOpen) {
      setSelected(currentInstructors as Instructor[]);
    } else {
      setSelected([]);
    }
  }, [isOpen]);

  const handleSelect = (instructor: InstructorOption) => {
    const alreadyExists = selected.find(item => item.id === instructor.value);
    if (!alreadyExists) {
      setSelected(prev => [...prev, instructor as unknown as Instructor]);
    }
  };

  const handleDelete = (instructor: Instructor) => {
    setSelected(prev => prev.filter(item => item.id !== instructor.id));
  };

  const handleApply = () => {
    updateSlotInstructors(
      slotId,
      selected.map(i => ({ id: i.id, name: i.name, code: i.code })),
    );
    onClose();
  };

  const handleClose = () => {
    setSelected(currentInstructors as Instructor[]);
    onClose();
  };

  return (
    <Drawer
      className={styles.assignInstructorDrawer}
      heightGap={visibleHeaderPixels}
      title={<Heading type="h3">{t("programming-edit.assign-instructor.title")}</Heading>}
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick
      footer={
        <div className={styles.assignInstructorDrawer_footer}>
          {isReadOnly ? (
            <Button variant="ghost" onClick={handleClose}>
              {t("programming-edit.assign-instructor.close")}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={handleClose}>
                {t("programming-edit.assign-instructor.cancel")}
              </Button>
              <Button variant="primary" onClick={handleApply}>
                {t("programming-edit.assign-instructor.assign")}
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className={styles.assignInstructorDrawer_content}>
        <InstructorPicker
          instructors={selected}
          loading={loading}
          options={options}
          shouldFilterInstructors={shouldFilter}
          canAssignFromOtherCourses={!!user?.abilities?.can_assign_instructors_from_other_courses}
          isReadOnly={isReadOnly}
          onSearch={onSearch}
          onFilterChange={setShouldFilter}
          onSelect={handleSelect}
          onDelete={handleDelete}
        />
      </div>
    </Drawer>
  );
};

export default AssignInstructorDrawer;
